import {
  Autorenew as Loader2,
  ChevronLeft,
  ChevronRight,
  FilterList as FilterIcon,
  KeyboardArrowDown as ChevronDown,
  KeyboardArrowUp as ChevronUp,
  Search,
  Settings,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface Column<T> {
  field: keyof T;
  title: string | React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  hidden?: boolean;
  width?: string;
  render?: (value: any, row: T) => React.ReactNode;
}

export interface Filter {
  field: string;
  value: string;
  operator?: 'equals' | 'contains' | 'startsWith' | 'endsWith';
}

export interface FilterDefinition {
  field: string;
  title: string;
  type?: 'text' | 'number' | 'select' | 'boolean' | 'date';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface DataTableProps<T> {
  data: T[] | ((params: QueryParams) => Promise<{ data: T[]; total: number }>);
  columns: Column<T>[];
  idKey?: keyof T;
  pageSize?: number;
  pageSizeOptions?: number[];
  searchable?: boolean;
  filterable?: boolean;
  title?: string;
  titleAction?: React.ReactNode;
  loading?: boolean;
  onRowClick?: (row: T, rowIndex: number) => void;
  initialFilters?: Filter[];
  onFiltersChange?: (filters: Filter[]) => void;
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  filterDefinitions?: FilterDefinition[];
  filterValidator?: (filter: Filter) => boolean; // Validates filters before data fetch
  filterDebounceMs?: number; // Debounce delay for filter changes in milliseconds (default: 0 = no debounce)
  tableKey?: string; // Unique key for local storage
  defaultHiddenColumns?: (keyof T)[]; // Default hidden columns
  infinitePagination?: boolean; // Enable infinite pagination mode
  rowStyles?: Map<any, React.CSSProperties>; // Map of row ID to custom styles
}

export interface QueryParams {
  page: number;
  pageSize: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  filters?: Filter[];
}

export const DataTable = <T extends Record<string, any>>({
  data,
  columns: initialColumns,
  idKey = 'id' as keyof T,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  searchable = true,
  filterable = true,
  title,
  titleAction,
  loading = false,
  onRowClick,
  initialFilters = [],
  onFiltersChange,
  initialSearch = '',
  onSearchChange,
  filterDefinitions,
  filterValidator,
  filterDebounceMs = 400,
  tableKey,
  defaultHiddenColumns = [],
  infinitePagination = false,
  rowStyles,
}: DataTableProps<T>) => {
  // Load column visibility from local storage or use defaults
  const getStoredColumnVisibility = useCallback(() => {
    if (!tableKey) return {};
    try {
      const stored = localStorage.getItem(`datatable-${tableKey}-columns`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load column settings:', e);
    }
    return {};
  }, [tableKey]);

  // Load column order from local storage
  const getStoredColumnOrder = useCallback(() => {
    if (!tableKey) return null;
    try {
      const stored = localStorage.getItem(`datatable-${tableKey}-order`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load column order:', e);
    }
    return null;
  }, [tableKey]);

  // Initialize columns with stored visibility and order
  const [columns, setColumns] = useState(() => {
    const storedVisibility = getStoredColumnVisibility();
    const storedOrder = getStoredColumnOrder();

    let cols = initialColumns.map((col) => ({
      ...col,
      hidden:
        storedVisibility[col.field as string] !== undefined
          ? storedVisibility[col.field as string]
          : defaultHiddenColumns.includes(col.field),
    }));

    // Apply stored order if available
    if (storedOrder && Array.isArray(storedOrder)) {
      const orderedCols: typeof cols = [];
      storedOrder.forEach((field: string) => {
        const col = cols.find((c) => c.field === field);
        if (col) orderedCols.push(col);
      });
      // Add any new columns that weren't in the stored order
      cols.forEach((col) => {
        if (!orderedCols.find((c) => c.field === col.field)) {
          orderedCols.push(col);
        }
      });
      cols = orderedCols;
    }

    return cols;
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState(initialSearch);
  const [orderBy, setOrderBy] = useState<keyof T | null>(null);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<Filter[]>(initialFilters);
  const [tempFilterValues, setTempFilterValues] = useState<Record<string, string>>(() => {
    // Initialize temp values from initial filters
    const temp: Record<string, string> = {};
    initialFilters.forEach((f) => {
      temp[f.field] = f.value;
    });
    return temp;
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [tableData, setTableData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<keyof T | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<keyof T | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const filterDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keyboard navigation for pagination
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!infinitePagination) return;

      // Check if user is typing in an input field
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' && page > 0 && !isLoading) {
        setPage((prev) => prev - 1);
      } else if (e.key === 'ArrowRight' && hasMore && !isLoading) {
        setPage((prev) => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [infinitePagination, page, hasMore, isLoading]);

  // Sync external filter/search changes
  useEffect(() => {
    setFilters(initialFilters);
    // Also update temp values when filters change externally
    const temp: Record<string, string> = {};
    initialFilters.forEach((f) => {
      temp[f.field] = f.value;
    });
    setTempFilterValues(temp);
  }, [initialFilters]);

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (filterDebounceTimeoutRef.current) {
        clearTimeout(filterDebounceTimeoutRef.current);
      }
    };
  }, []);

  const validFilters = useMemo(() => {
    return filterValidator ? filters.filter(filterValidator) : filters;
  }, [filters, filterValidator]);

  const validFiltersKey = useMemo(() => {
    return JSON.stringify(validFilters);
  }, [validFilters]);

  // Handle async data fetching
  useEffect(() => {
    const fetchData = async () => {
      if (typeof data === 'function') {
        setIsLoading(true);
        try {
          const result = await data({
            page,
            pageSize,
            search,
            orderBy: orderBy as string,
            orderDirection,
            filters: validFilters,
          });

          if (infinitePagination) {
            // Show only current page data
            setTableData(result.data);
            setHasMore(result.data.length >= pageSize);
          } else {
            setTableData(result.data);
          }

          setTotalCount(result.total);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setTableData(data);
        setTotalCount(data.length);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page, pageSize, search, orderBy, orderDirection, validFiltersKey, infinitePagination]);

  // Reset page when search or filters change in infinite pagination mode
  useEffect(() => {
    if (infinitePagination) {
      setPage(0);
      setHasMore(true);
    }
  }, [search, validFiltersKey, orderBy, orderDirection, infinitePagination]);

  // Process data for static datasets
  const processedData = useMemo(() => {
    if (typeof data === 'function') {
      return tableData;
    }

    let filtered = [...tableData];

    // Apply search
    if (search) {
      filtered = filtered.filter((row) =>
        columns.some((col) => {
          const value = row[col.field];
          return value?.toString().toLowerCase().includes(search.toLowerCase());
        })
      );
    }

    // Apply filters
    filters.forEach((filter) => {
      filtered = filtered.filter((row) => {
        const value = row[filter.field as keyof T];
        const filterValue = filter.value.toLowerCase();
        const rowValue = value?.toString().toLowerCase() || '';

        switch (filter.operator) {
          case 'equals':
            return rowValue === filterValue;
          case 'startsWith':
            return rowValue.startsWith(filterValue);
          case 'endsWith':
            return rowValue.endsWith(filterValue);
          case 'contains':
          default:
            return rowValue.includes(filterValue);
        }
      });
    });

    // Apply sorting
    if (orderBy) {
      filtered.sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];

        if (aVal === bVal) return 0;

        const comparison = aVal < bVal ? -1 : 1;
        return orderDirection === 'asc' ? comparison : -comparison;
      });
    }

    // Update total count for pagination
    if (typeof data !== 'function') {
      setTotalCount(filtered.length);
    }

    // Apply pagination
    const start = page * pageSize;
    const end = start + pageSize;
    return filtered.slice(start, end);
  }, [tableData, search, filters, orderBy, orderDirection, page, pageSize, columns, data]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSort = useCallback(
    (field: keyof T) => {
      if (orderBy === field) {
        setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setOrderBy(field);
        setOrderDirection('asc');
      }
    },
    [orderBy, orderDirection]
  );

  const handleFilterChange = (field: string, value: string) => {
    // Update temp values immediately for responsive UI
    setTempFilterValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear existing debounce timeout
    if (filterDebounceTimeoutRef.current) {
      clearTimeout(filterDebounceTimeoutRef.current);
    }

    // Function to update actual filters
    const updateFilters = () => {
      setFilters((prev) => {
        const existing = prev.find((f) => f.field === field);
        let newFilters: Filter[];
        if (value) {
          if (existing) {
            newFilters = prev.map((f) => (f.field === field ? { ...f, value } : f));
          } else {
            newFilters = [...prev, { field, value, operator: 'contains' }];
          }
        } else {
          newFilters = prev.filter((f) => f.field !== field);
        }
        onFiltersChange?.(newFilters);
        return newFilters;
      });
      setPage(0);
    };

    // If debounce is enabled, delay the filter update
    if (filterDebounceMs > 0) {
      filterDebounceTimeoutRef.current = setTimeout(updateFilters, filterDebounceMs);
    } else {
      // No debounce, update immediately
      updateFilters();
    }
  };

  const toggleColumnVisibility = useCallback(
    (field: keyof T) => {
      setColumns((prev) => {
        const updated = prev.map((col) =>
          col.field === field ? { ...col, hidden: !col.hidden } : col
        );

        // Save to local storage if tableKey is provided
        if (tableKey) {
          try {
            const visibility = updated.reduce(
              (acc, col) => ({
                ...acc,
                [col.field as string]: col.hidden || false,
              }),
              {}
            );
            localStorage.setItem(`datatable-${tableKey}-columns`, JSON.stringify(visibility));
          } catch (e) {
            console.error('Failed to save column settings:', e);
          }
        }

        return updated;
      });
    },
    [tableKey]
  );

  const visibleColumns = columns.filter((col) => !col.hidden);

  // Handle column drag start
  const handleDragStart = (e: React.DragEvent<HTMLTableHeaderCellElement>, field: keyof T) => {
    setDraggedColumn(field);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle column drag over
  const handleDragOver = (e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle column drag enter
  const handleDragEnter = (field: keyof T) => {
    if (draggedColumn && field !== draggedColumn) {
      setDragOverColumn(field);
    }
  };

  // Handle column drag leave
  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  // Handle column drop
  const handleDrop = (e: React.DragEvent<HTMLTableHeaderCellElement>, targetField: keyof T) => {
    e.preventDefault();

    if (!draggedColumn || draggedColumn === targetField) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    setColumns((prev) => {
      const newColumns = [...prev];
      const draggedIndex = newColumns.findIndex((col) => col.field === draggedColumn);
      const targetIndex = newColumns.findIndex((col) => col.field === targetField);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [draggedCol] = newColumns.splice(draggedIndex, 1);
        newColumns.splice(targetIndex, 0, draggedCol);

        // Save new order to localStorage
        if (tableKey) {
          try {
            const order = newColumns.map((col) => col.field as string);
            localStorage.setItem(`datatable-${tableKey}-order`, JSON.stringify(order));
          } catch (e) {
            console.error('Failed to save column order:', e);
          }
        }
      }

      return newColumns;
    });

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md ${
        infinitePagination ? 'flex flex-col h-full overflow-hidden' : ''
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">{title || 'Data Table'}</h2>
            {titleAction}
          </div>
          <div className="flex items-center gap-2">
            {searchable && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    const newSearch = e.target.value;
                    setSearch(newSearch);
                    setPage(0);
                    onSearchChange?.(newSearch);
                  }}
                  className="pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search..."
                />
              </div>
            )}
            {filterable && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-md transition-colors ${
                  showFilters ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'
                }`}
              >
                <FilterIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className={`p-2 rounded-md transition-colors ${
                showColumnSettings ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'
              }`}
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h3 className="text-sm font-semibold mb-2">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(filterDefinitions || columns.filter((col) => col.filterable !== false)).map(
                (filterDef) => {
                  const isFilterDef =
                    'field' in filterDef && 'title' in filterDef && !('sortable' in filterDef);
                  const field = isFilterDef
                    ? (filterDef as FilterDefinition).field
                    : ((filterDef as Column<T>).field as string);
                  const title = isFilterDef
                    ? (filterDef as FilterDefinition).title
                    : (filterDef as Column<T>).title;
                  const type = isFilterDef ? (filterDef as FilterDefinition).type : 'text';
                  const options = isFilterDef ? (filterDef as FilterDefinition).options : undefined;
                  const placeholder = isFilterDef
                    ? (filterDef as FilterDefinition).placeholder
                    : `Filter ${title}...`;
                  const currentValue = tempFilterValues[field] || '';

                  return (
                    <div key={field}>
                      <label className="block text-xs text-gray-600 mb-1">{title}</label>
                      {type === 'select' && options ? (
                        <select
                          value={currentValue}
                          onChange={(e) => handleFilterChange(field, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="">{placeholder || 'All'}</option>
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : type === 'boolean' ? (
                        <select
                          value={currentValue}
                          onChange={(e) => handleFilterChange(field, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="">All</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : type === 'date' ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            value={currentValue ? dayjs(currentValue) : null}
                            onChange={(newValue: Dayjs | null) => {
                              handleFilterChange(
                                field,
                                newValue ? newValue.format('YYYY-MM-DD') : ''
                              );
                            }}
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: {
                                size: 'small',
                                placeholder: placeholder || 'Select date',
                                sx: {
                                  '& .MuiInputBase-root': {
                                    fontSize: '0.875rem',
                                    height: '30px',
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: '4px 8px',
                                  },
                                },
                              },
                              field: { clearable: true },
                            }}
                          />
                        </LocalizationProvider>
                      ) : (
                        <input
                          type={type === 'number' ? 'number' : 'text'}
                          value={currentValue}
                          onChange={(e) => handleFilterChange(field, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder={placeholder}
                        />
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

        {/* Column Settings Panel */}
        {showColumnSettings && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold">Column Visibility</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {columns.filter((c) => !c.hidden).length} of {columns.length} columns visible
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Show all columns
                    setColumns((prev) => {
                      const updated = prev.map((col) => ({
                        ...col,
                        hidden: false,
                      }));
                      if (tableKey) {
                        const visibility = updated.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.field as string]: false,
                          }),
                          {}
                        );
                        localStorage.setItem(
                          `datatable-${tableKey}-columns`,
                          JSON.stringify(visibility)
                        );
                      }
                      return updated;
                    });
                  }}
                  className="text-xs text-green-600 hover:text-green-800"
                >
                  Show all
                </button>
                <button
                  onClick={() => {
                    // Hide all columns
                    setColumns((prev) => {
                      const updated = prev.map((col) => ({
                        ...col,
                        hidden: true,
                      }));
                      if (tableKey) {
                        const visibility = updated.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.field as string]: true,
                          }),
                          {}
                        );
                        localStorage.setItem(
                          `datatable-${tableKey}-columns`,
                          JSON.stringify(visibility)
                        );
                      }
                      return updated;
                    });
                  }}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Hide all
                </button>
                {tableKey && (
                  <button
                    onClick={() => {
                      // Reset to default visibility and order
                      setColumns(
                        initialColumns.map((col) => ({
                          ...col,
                          hidden: defaultHiddenColumns.includes(col.field),
                        }))
                      );
                      if (tableKey) {
                        localStorage.removeItem(`datatable-${tableKey}-columns`);
                        localStorage.removeItem(`datatable-${tableKey}-order`);
                      }
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Reset to defaults
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-3">Drag columns to reorder them</p>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {columns.map((col, index) => (
                  <div
                    key={col.field as string}
                    draggable
                    onDragStart={(e) => handleDragStart(e as any, col.field)}
                    onDragOver={handleDragOver as any}
                    onDragEnter={() => handleDragEnter(col.field)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e as any, col.field)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center justify-between p-2 bg-white border rounded cursor-move hover:bg-gray-50 ${
                      dragOverColumn === col.field ? 'border-blue-500 border-2' : 'border-gray-200'
                    } ${draggedColumn === col.field ? 'opacity-50' : ''}`}
                  >
                    <label className="flex items-center flex-1 cursor-move">
                      <svg
                        className="w-4 h-4 text-gray-400 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8h16M4 16h16"
                        />
                      </svg>
                      <input
                        type="checkbox"
                        checked={!col.hidden}
                        onChange={() => toggleColumnVisibility(col.field)}
                        className="mr-2"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-sm">{col.title}</span>
                    </label>
                    <span className="text-xs text-gray-400">#{index + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className={`relative ${infinitePagination ? 'flex flex-col flex-1 min-h-0' : ''}`}>
        {/* Loading Overlay */}
        {(isLoading || loading) && processedData.length > 0 && (
          <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}

        {infinitePagination ? (
          <>
            {/* Fixed Header for Infinite Pagination */}
            <div
              ref={headerRef}
              className="overflow-x-auto overflow-y-hidden flex-shrink-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              onScroll={(e) => {
                if (bodyRef.current) {
                  bodyRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
            >
              <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  {visibleColumns.map((column) => (
                    <col key={column.field as string} style={{ width: column.width || 'auto' }} />
                  ))}
                </colgroup>
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.field as string}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                          column.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                        } ${dragOverColumn === column.field ? 'border-l-4 border-blue-500' : ''} ${
                          draggedColumn === column.field ? 'opacity-50' : ''
                        }`}
                        style={{ width: column.width }}
                        onClick={() => column.sortable !== false && handleSort(column.field)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, column.field)}
                        onDragOver={handleDragOver}
                        onDragEnter={() => handleDragEnter(column.field)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.field)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="flex items-center justify-between select-none">
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3 h-3 text-gray-400 cursor-move"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8h16M4 16h16"
                              />
                            </svg>
                            {column.title}
                          </span>
                          {column.sortable !== false &&
                            orderBy === column.field &&
                            (orderDirection === 'asc' ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            ))}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>

            {/* Scrollable Body for Infinite Pagination */}
            <div
              ref={bodyRef}
              className="overflow-auto flex-1"
              onScroll={(e) => {
                if (headerRef.current) {
                  headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
                }
              }}
            >
              <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  {visibleColumns.map((column) => (
                    <col key={column.field as string} style={{ width: column.width || 'auto' }} />
                  ))}
                </colgroup>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedData.length === 0 && !(isLoading || loading) ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No data available
                      </td>
                    </tr>
                  ) : processedData.length === 0 && (isLoading || loading) ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                          <span>Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    processedData.map((row, rowIndex) => {
                      const rowId = row[idKey];
                      const rowStyle = rowStyles?.get(rowId);
                      return (
                        <tr
                          key={rowId || rowIndex}
                          className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                          style={rowStyle}
                          onClick={() => onRowClick?.(row, rowIndex)}
                        >
                          {visibleColumns.map((column) => (
                            <td
                              key={column.field as string}
                              className="px-4 py-3 text-sm text-gray-900 break-words whitespace-normal overflow-hidden"
                            >
                              {column.render
                                ? column.render(row[column.field], row)
                                : row[column.field]?.toString() || '-'}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          /* Regular Table for Pagination Mode */
          <div className="overflow-x-auto">
            <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                {visibleColumns.map((column) => (
                  <col key={column.field as string} style={{ width: column.width || 'auto' }} />
                ))}
              </colgroup>
              <thead className="bg-gray-50 border-b">
                <tr>
                  {visibleColumns.map((column) => (
                    <th
                      key={column.field as string}
                      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        column.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                      } ${
                        dragOverColumn === column.field ? 'border-l-4 border-blue-500' : ''
                      } ${draggedColumn === column.field ? 'opacity-50' : ''}`}
                      style={{ width: column.width }}
                      onClick={() => column.sortable !== false && handleSort(column.field)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, column.field)}
                      onDragOver={handleDragOver}
                      onDragEnter={() => handleDragEnter(column.field)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, column.field)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="flex items-center justify-between select-none">
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-3 h-3 text-gray-400 cursor-move"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8h16M4 16h16"
                            />
                          </svg>
                          {column.title}
                        </span>
                        {column.sortable !== false &&
                          orderBy === column.field &&
                          (orderDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          ))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processedData.length === 0 && !(isLoading || loading) ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                ) : processedData.length === 0 && (isLoading || loading) ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                        <span>Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  processedData.map((row, rowIndex) => {
                    const rowId = row[idKey];
                    const rowStyle = rowStyles?.get(rowId);
                    return (
                      <tr
                        key={rowId || rowIndex}
                        className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                        style={rowStyle}
                        onClick={() => onRowClick?.(row, rowIndex)}
                      >
                        {visibleColumns.map((column) => (
                          <td
                            key={column.field as string}
                            className="px-4 py-3 text-sm text-gray-900 break-words whitespace-normal overflow-hidden"
                          >
                            {column.render
                              ? column.render(row[column.field], row)
                              : row[column.field]?.toString() || '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination or Infinite Pagination Controls */}
      {infinitePagination ? (
        <div className="px-4 py-3 bg-gray-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                    setHasMore(true);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-sm text-gray-700">
                Showing {tableData.length} items
                {totalCount && ` of ${totalCount} total`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Use ← → arrow keys to navigate</span>
              <div className="border-l pl-2 flex items-center gap-2">
                <button
                  onClick={() => setPage(0)}
                  disabled={page === 0 || isLoading}
                  className="px-3 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Go to first page (Home)"
                >
                  First
                </button>
                <button
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page === 0 || isLoading}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous page (← Arrow)"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="px-3 py-1 text-sm font-medium bg-white border rounded">
                  Page {page + 1}
                </span>

                <button
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={!hasMore || isLoading}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!hasMore ? 'No more pages' : 'Next page (→ Arrow)'}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                {isLoading && (
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-3 bg-gray-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">
                {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
              </span>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages - 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
